/* eslint-disable jsx-a11y/alt-text */
import React from 'react';
import styled, { useTheme } from 'styled-components';
import ImageBW from '../../components/ImageBoundsWrapper';
import theme from 'shared/assets/style/theme';
import { getText, TEXT_KEY } from 'shared/utils/localization';
import { ASSET_KEY, assetSource } from 'shared/utils/assetSource';
import { purchaseActionTriggers } from 'shared/constants/userActions';
import Button from '../../components/Button/Button';
import generateRender from 'shared/utils/generateRender';
import { BUTTON_STROKES } from 'shared/utils/buttonTextStrokes';
import LazyImage from '../../components/LazyImage/LazyImage';
import { TAG, STORE_COIN_ROW_VARIANTS } from 'shared/constants';
import { amountAndPercentageFinder } from 'shared/screens/Shop/utils/amountAndPercentageHelper';
import ExclusiveSaleBadge from './components/ExclusiveSaleBadge';
import useFeatureFlags from 'shared/hooks/useFeatureFlags';
import { FE_LOYALTY_ICON_BONUS_BADGE } from 'shared/utils/featureFlags/constants';

const assets = {
  purple: {
    backing: assetSource(ASSET_KEY.ITEM_ROW_PURPLE),
    left: assetSource(ASSET_KEY.SHOP_PURPLE_CAP),
    triangle: assetSource(ASSET_KEY.SHOP_PURPLE_TRIANGLE_DIVIDER),
  },
  gold: {
    backing: assetSource(ASSET_KEY.ITEM_ROW_GOLD),
    left: assetSource(ASSET_KEY.SHOP_GOLD_CAP),
    triangle: assetSource(ASSET_KEY.SHOP_GOLD_TRIANGLE_DIVIDER),
  },
  ibw: {
    backing: require(asset`store-item-backing.ibw`),
    left: require(asset`store-item-left-end.ibw`),
    triangle: require(asset`store-item-triangle.ibw`),
    upsell: require(asset`store-item-upsell.ibw`),
  },
  upsell: {
    amazing: assetSource(ASSET_KEY.UPSELL_TAB_AMAZING),
    'best deal': assetSource(ASSET_KEY.UPSELL_TAB_BEST_DEAL),
    popular: assetSource(ASSET_KEY.UPSELL_TAB_POPULAR),
  },
};

/** @typedef {import('shared/api/Constants').CurrencyPackageItemKeys} CPIK */

export default function CoinItem({
  item = {},
  buyHandle,
  showPromoBoost = false,
  assetsUrls,
  hasExclusiveSalePackages = false,
}) {
  const themeContext = useTheme();
  const { coinAmount, percent, exclusiveSalePercentage, itemOnSale } = amountAndPercentageFinder({
    showPromoBoost,
    item,
  });
  // TODO: trigger gold with some variable that likely doesn't exist at time of writing
  const variant = item.CORNER_FLAG ? STORE_COIN_ROW_VARIANTS.GOLD : STORE_COIN_ROW_VARIANTS.PURPLE;

  const buyButtonSrc = generateRender({
    default: assetSource(ASSET_KEY.ROUNDED_RECT_PRIMARY),
  });

  const { hasFeature } = useFeatureFlags();
  const hasBonusLoyaltyBadge = hasFeature(FE_LOYALTY_ICON_BONUS_BADGE);
  const loyaltyIconKey = hasBonusLoyaltyBadge
    ? ASSET_KEY.LOYALTY_ICON_BONUS_BADGE
    : ASSET_KEY.LOYALTY_ICON_BONUS_REGULAR;

  /*
   * Absolutely positioned centering calculations.
   * Result is icon positioned absolutely at the intersection of left inset and the rest of the bar.
   * * Since these are using two different units, it has to be evaluated with CSS's calc().
   */

  return (
    <ImageBW
      source={assets[variant].backing}
      ibw={assets.ibw.backing}
      manualAssetScale={3}
      style={themeContext.ShopScreen.ShopContainer.PackagesContainer.CoinItem}
      innerStyle={themeContext.ShopScreen.ShopContainer.PackagesContainer.InnerItem}
    >
      <ImageBW
        source={assets[variant].left}
        ibw={assets.ibw.left}
        manualAssetScale={3}
        style={
          hasBonusLoyaltyBadge
            ? variant === STORE_COIN_ROW_VARIANTS.GOLD
              ? themeContext.CoinItem.LoyaltyIconGoldapWithBadge
              : themeContext.CoinItem.LoyaltyIconCapWithBadge
            : styles.loyaltyIcon
        }
        innerStyle={styles.loyaltyIconInner}
      >
        <div style={themeContext.ShopScreen.ShopContainer.PackagesContainer.InnerItem.Stack}>
          <ImageBW
            source={assetSource(loyaltyIconKey)}
            ibw={require(asset`loyalty-icon.ibw`)}
            style={themeContext.ShopScreen.ShopContainer.PackagesContainer.InnerItem.StackLoyaltyIcon}
          />
          <div
            style={{
              ...themeContext.ShopScreen.ShopContainer.PackagesContainer.InnerItem.StackAmount,
              color: variant === STORE_COIN_ROW_VARIANTS.PURPLE ? theme.palette.common[1] : theme.palette.common[0],
            }}
          >
            {item.BONUS_LOYALTY_AMOUNT}
          </div>
        </div>
      </ImageBW>
      {!hasBonusLoyaltyBadge ? (
        <ImageBW
          source={assets[variant].triangle}
          ibw={assets.ibw.triangle}
          manualAssetScale={3}
          style={styles.triangleBox}
          innerStyle={styles.triangleBoxInner}
        >
          <div
            style={{
              fontSize: 24,
              fontWeight: 600,
              color: variant === STORE_COIN_ROW_VARIANTS.PURPLE ? theme.palette.common[1] : theme.palette.common[0],
              marginRight: 5,
            }}
          >
            +
          </div>
        </ImageBW>
      ) : (
        <EmptyTriangleBox />
      )}
      <CoinPackageWrapper>
        <CoinPackage tag={TAG.IMG} src={`${assetsUrls.ImagesCdnBaseUrl}/marketing/coinpkg/${item.IMAGE}`} />
        {item.CORNER_FLAG && (
          <FlagIcon tag={TAG.IMG} src={assets.upsell[item.CORNER_FLAG] || assets.upsell['amazing']} />
        )}
      </CoinPackageWrapper>
      {hasExclusiveSalePackages && <ExclusiveSaleBadge percent={exclusiveSalePercentage} />}
      <PricTexteWrapper className="stack">
        <div
          style={{
            ...themeContext.CoinItem.CoinAmount,
            color: item.CORNER_FLAG ? theme.palette.common[0] : theme.palette.common[1],
          }}
        >
          {coinAmount}
        </div>
        {((showPromoBoost && !itemOnSale) || (percent > 0 && !itemOnSale)) && (
          <div
            style={{
              ...themeContext.CoinItem.Extra,
              color: item.CORNER_FLAG ? theme.palette.common[4] : theme.palette.primary[13],
            }}
          >
            {percent}
            {getText(TEXT_KEY.EXTRA_PERCENTAGE)}
          </div>
        )}
        {!showPromoBoost && itemOnSale && (
          <div
            style={{
              ...themeContext.CoinItem.Extra,
              color: item.CORNER_FLAG ? theme.palette.common[0] : theme.palette.common[1],
              whiteSpace: 'nowrap',
            }}
          >
            <span>{getText(TEXT_KEY.SALE)}!</span> {getText(TEXT_KEY.WAS)}{' '}
            <span style={{ textDecoration: 'line-through' }}>{item.ORIGINAL_AMOUNT}</span>
          </div>
        )}
        {showPromoBoost && itemOnSale && (
          <div
            style={{
              ...themeContext.CoinItem.Extra,
              color: item.CORNER_FLAG ? theme.palette.common[4] : theme.palette.primary[13],
            }}
          >
            <span>
              {parseInt(item.PROMO_BOOST_PERCENTAGE) +
                (parseInt(item.GENERIC_SALE_PERCENTAGE) || parseInt(exclusiveSalePercentage))}{' '}
              {getText(TEXT_KEY.EXTRA_PERCENTAGE)}
            </span>
          </div>
        )}
      </PricTexteWrapper>
      <div
        style={{
          width: '20vmin',
          color: item.CORNER_FLAG ? theme.palette.common[0] : theme.palette.common[1],
          textAlign: 'center',
          paddingRight: 0,
        }}
      >
        <p style={{ fontSize: '1.5rem', margin: 0 }}>{`${getText(TEXT_KEY.CURRENCY_SIGN)}${item?.PRICE}`}</p>
      </div>
      <Button
        imageStyle={styles.buyButtonImage}
        textStroke={BUTTON_STROKES.GREEN}
        imageSource={buyButtonSrc}
        label={getText(TEXT_KEY.BUY)}
        textStyle={themeContext.CoinItem.BuyButtonText}
        onClick={() =>
          buyHandle(item.CURRENCY_ITEM_ID, item.NAME, null, item.PRICE, purchaseActionTriggers.STORE_COIN_ITEM)
        }
      />
    </ImageBW>
  );
}

const PricTexteWrapper = styled.div`
  flex: 1;
  text-align: center;
`;

const CoinPackageWrapper = styled.div`
  position: relative;
  height: 100%;
  width: 20vmin;
`;

const CoinPackage = styled(LazyImage)`
  display: block;
  width: 100%;
  height: 100%;
  object-fit: contain;
`;

const EmptyTriangleBox = styled.div`
  width: 1.5vmin;
  height: 100%;
  ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.device('mobile-browser') &&
    `
    width: 1.5vmin;
  `}
`;

const FlagIcon = styled(LazyImage)`
  position: absolute;
  top: -6px;
  left: -0.5vw;
  right: 17px;
  height: 2.5vh;
  object-fit: contain;
  ${({ theme }) => theme.breakpoints.down('md')} {
    left: -0.5vw;
    height: 2vh;
  }
  ${({ theme }) =>
    theme.platform('android') &&
    theme.screens.devices(['mobile-browser', 'mobile-standalone']) &&
    `
    left: -0.4vw;
    height: 2.5vh;
    max-width: 5vw;
    top: -0.5vh;
  `}
`;

const styles = {
  buyButtonImage: {
    objectFit: 'contain',
    width: '7vw',
    minWidth: 100,
    maxWidth: 130,
    pointerEvents: 'all',
    cursor: 'pointer',
  },
  loyaltyIcon: {
    width: '12vmin',
    height: '100%',
    objectFit: 'stretch',
  },
  loyaltyIconWithBonus: {
    width: '12vmin',
    height: '96%',
    objectFit: 'stretch',
  },
  loyaltyIconInner: {
    display: 'flex',
    width: '100%',
    alignItems: 'center',
    justifyContent: 'space-evenly',
  },
  triangleBox: {
    width: '3vmin',
    height: '100%',
    objectFit: 'stretch',
  },
  triangleBoxInner: {
    height: '100%',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
};

